import { extractVariantOptionValues } from "./variant-options"
import { getVariantIdentifier } from "./variants"

export type VariantSelection = Record<string, string>

type VariantMatchOptions = {
  requireAllOptions?: boolean
}

const normalizeComparable = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined
  const normalized = value.trim().toLowerCase()
  return normalized.length ? normalized : undefined
}

const toFiniteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

export const getOptionKeyCandidates = (option: any): string[] => {
  const candidates = [
    option?.name,
    option?.title,
    option?.id,
    option?._id,
  ]
    .map((value) => {
      if (typeof value === "string") return value.trim()
      if (typeof value === "number" && Number.isFinite(value)) {
        return String(value)
      }
      return ""
    })
    .filter(Boolean)

  return Array.from(new Set(candidates))
}

export const getOptionValues = (option: any): string[] => {
  const values = (Array.isArray(option?.values) ? option.values : [])
    .map((entry: any) =>
      typeof entry === "string" || typeof entry === "number"
        ? String(entry).trim()
        : typeof entry?.value === "string" || typeof entry?.value === "number"
          ? String(entry.value).trim()
          : ""
    )
    .filter(Boolean)

  return Array.from(new Set(values))
}

const readValueForCandidates = (
  values: Record<string, string> | undefined,
  candidates: string[]
): string | undefined => {
  if (!values) return undefined

  for (const candidate of candidates) {
    const value = values[candidate]
    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
  }

  const normalizedCandidates = new Set(
    candidates.map(normalizeComparable).filter(Boolean)
  )
  for (const [key, value] of Object.entries(values)) {
    if (
      normalizedCandidates.has(normalizeComparable(key)) &&
      typeof value === "string" &&
      value.trim()
    ) {
      return value.trim()
    }
  }

  return undefined
}

export const getSelectedOptionValue = (
  selection: VariantSelection,
  option: any
): string | undefined =>
  readValueForCandidates(selection, getOptionKeyCandidates(option))

export const getVariantInventoryState = (product: any, variant?: any) => {
  const trackInventory =
    typeof variant?.trackInventory === "boolean"
      ? variant.trackInventory
      : product?.trackInventory === true

  const stock = variant
    ? [
        variant?.stock,
        variant?.inventory_quantity,
        variant?.inventoryQuantity,
        variant?.available_quantity,
        variant?.availableQuantity,
      ]
        .map(toFiniteNumber)
        .find((value) => typeof value === "number")
    : toFiniteNumber(product?.stock)

  return {
    trackInventory,
    stock,
    available: !trackInventory || (typeof stock === "number" && stock > 0),
  }
}

export const isVariantAvailable = (
  product: any,
  variant: any,
  quantity = 1
) => {
  if (!variant || typeof variant !== "object") return false
  if (variant.active === false) return false
  const inventory = getVariantInventoryState(product, variant)
  if (!inventory.trackInventory) return true
  return (
    typeof inventory.stock === "number" &&
    inventory.stock >= Math.max(1, Math.floor(quantity) || 1)
  )
}

export const variantMatchesSelection = (
  productOptions: any[],
  variant: any,
  selection: VariantSelection,
  { requireAllOptions = true }: VariantMatchOptions = {}
) => {
  if (!variant || !Array.isArray(productOptions)) return false
  const variantValues = extractVariantOptionValues(variant)
  if (!variantValues) return false

  let comparedOptions = 0
  for (const option of productOptions) {
    const candidates = getOptionKeyCandidates(option)
    if (!candidates.length) continue

    const selectedValue = readValueForCandidates(selection, candidates)
    if (!selectedValue) {
      if (requireAllOptions) return false
      continue
    }

    const variantValue = readValueForCandidates(variantValues, candidates)
    if (
      !variantValue ||
      normalizeComparable(variantValue) !== normalizeComparable(selectedValue)
    ) {
      return false
    }
    comparedOptions += 1
  }

  return comparedOptions > 0 || productOptions.length === 0
}

export const findVariantForSelection = (
  productOptions: any[],
  variants: any[],
  selection: VariantSelection
) =>
  variants.find((variant) =>
    variantMatchesSelection(productOptions, variant, selection)
  ) || null

export const selectDefaultAvailableVariant = ({
  product,
  variants,
  requestedVariantId,
}: {
  product: any
  variants: any[]
  requestedVariantId?: string | null
}) => {
  const availableVariants = variants.filter((variant) =>
    isVariantAvailable(product, variant)
  )
  if (!availableVariants.length) return undefined

  const normalizedRequestedId = requestedVariantId?.trim()
  if (normalizedRequestedId) {
    const requestedVariant = availableVariants.find(
      (variant) => getVariantIdentifier(variant) === normalizedRequestedId
    )
    if (requestedVariant) return requestedVariant
  }

  return (
    availableVariants.find(
      (variant) =>
        variant?.isDefault === true ||
        variant?.is_default === true ||
        variant?.default === true ||
        variant?.defaultVariant === true
    ) || availableVariants[0]
  )
}

export const buildSelectionFromVariant = (
  productOptions: any[],
  variant: any
): VariantSelection => {
  const variantValues = extractVariantOptionValues(variant)
  if (!variantValues) return {}

  return productOptions.reduce((selection, option) => {
    const candidates = getOptionKeyCandidates(option)
    const value = readValueForCandidates(variantValues, candidates)
    if (!value) return selection
    candidates.forEach((candidate) => {
      selection[candidate] = value
    })
    return selection
  }, {} as VariantSelection)
}

export const getUnavailableOptionValues = ({
  product,
  variants,
  option,
}: {
  product: any
  productOptions: any[]
  variants: any[]
  option: any
  selection: VariantSelection
}) => {
  const optionCandidates = getOptionKeyCandidates(option)

  return getOptionValues(option).filter((value) => {
    return !variants.some(
      (variant) => {
        if (!isVariantAvailable(product, variant)) return false
        const variantValues = extractVariantOptionValues(variant)
        const variantValue = readValueForCandidates(
          variantValues,
          optionCandidates
        )
        return normalizeComparable(variantValue) === normalizeComparable(value)
      }
    )
  })
}

export const resolveSelectionForOptionChange = ({
  product,
  productOptions,
  variants,
  selection,
  option,
  value,
}: {
  product: any
  productOptions: any[]
  variants: any[]
  selection: VariantSelection
  option: any
  value: string
}): VariantSelection => {
  const changedOptionCandidates = getOptionKeyCandidates(option)
  const matchingVariants = variants.filter((variant) => {
    if (!isVariantAvailable(product, variant)) return false
    const variantValues = extractVariantOptionValues(variant)
    const variantValue = readValueForCandidates(
      variantValues,
      changedOptionCandidates
    )
    return normalizeComparable(variantValue) === normalizeComparable(value)
  })

  if (matchingVariants.length) {
    const ranked = matchingVariants
      .map((variant, index) => {
        const variantValues = extractVariantOptionValues(variant)
        const score = productOptions.reduce((total, currentOption) => {
          if (currentOption === option) return total
          const candidates = getOptionKeyCandidates(currentOption)
          const selectedValue = readValueForCandidates(selection, candidates)
          const variantValue = readValueForCandidates(variantValues, candidates)
          return selectedValue &&
            normalizeComparable(selectedValue) === normalizeComparable(variantValue)
            ? total + 1
            : total
        }, 0)
        return { variant, score, index }
      })
      .sort((left, right) => right.score - left.score || left.index - right.index)

    return buildSelectionFromVariant(productOptions, ranked[0].variant)
  }

  const next = { ...selection }
  changedOptionCandidates.forEach((candidate) => {
    next[candidate] = value
  })
  return next
}
